import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export type SocialPostStatus = "draft" | "approved" | "scheduled" | "published" | "failed";
export type SocialVariantStatus =
  | "draft" | "approved" | "scheduled" | "publishing" | "published" | "published_manual" | "failed" | "skipped";

export interface SocialPostVariant {
  id: string;
  post_id: string;
  network: "linkedin" | "x" | "facebook" | "instagram" | "tiktok";
  body: string;
  asset_url: string | null;
  hashtags: string[];
  status: SocialVariantStatus;
  scheduled_for: string | null;
  published_at: string | null;
  external_post_url: string | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
}

export interface SocialPost {
  id: string;
  source_kind: "idea" | "article" | "event";
  source_id: string | null;
  source_title: string | null;
  source_url: string | null;
  source_image_url: string | null;
  status: SocialPostStatus;
  scheduled_for: string | null;
  created_by: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export function useSocialPosts() {
  return useQuery({
    queryKey: ["social_posts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("social_posts")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as SocialPost[];
    },
  });
}

export function usePostVariants(postId: string | null) {
  return useQuery({
    queryKey: ["social_post_variants", postId],
    enabled: !!postId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("social_post_variants")
        .select("*")
        .eq("post_id", postId!)
        .order("network");
      if (error) throw error;
      return (data ?? []) as unknown as SocialPostVariant[];
    },
  });
}

export function useGenerateDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      source_kind: "idea" | "article" | "event";
      source_id: string;
    }) => {
      const { data, error } = await supabase.functions.invoke("social-generate-drafts", {
        body: args,
      });
      if (error) throw error;
      return data as { post_id: string };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["social_posts"] });
      toast.success("Drafts generated");
    },
    onError: (e: Error) => toast.error(e.message || "Could not generate drafts"),
  });
}

export function useUpdateVariant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; patch: Partial<SocialPostVariant> }) => {
      const { error } = await supabase
        .from("social_post_variants")
        .update(args.patch as any)
        .eq("id", args.id);
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["social_post_variants"] });
      qc.invalidateQueries({ queryKey: ["social_posts"] });
    },
  });
}

export function useDeletePost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("social_posts")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["social_posts"] });
      toast.success("Post deleted");
    },
  });
}

export function usePublishVariant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (variantId: string) => {
      const { data, error } = await supabase.functions.invoke("social-publish", {
        body: { variant_id: variantId },
      });
      if (error) throw error;
      if (data && data.ok === false) throw new Error(data.error || "Publish failed");
      return data as { ok: true; externalUrl: string | null };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["social_post_variants"] });
    },
    onError: (e: Error) => toast.error(e.message || "Publish failed"),
  });
}

export function useSocialSettings() {
  return useQuery({
    queryKey: ["social_settings"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("social_settings")
        .select("*")
        .eq("id", 1)
        .maybeSingle();
      if (error) throw error;
      return data as unknown as {
        id: number;
        auto_draft_ideas: boolean;
        auto_draft_articles: boolean;
        auto_draft_events: boolean;
        default_hashtags: string[];
        signature: string | null;
      } | null;
    },
  });
}

export function useUpdateSocialSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: Record<string, unknown>) => {
      const { error } = await supabase
        .from("social_settings")
        .update(patch as any)
        .eq("id", 1);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["social_settings"] });
      toast.success("Settings saved");
    },
  });
}